import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Trash2, Plus } from 'lucide-react';
import type { CaseStudy } from '@dj/types';
import { apiFetch } from '@/lib/api';

export default function CaseStudiesListPage() {
  const [studies, setStudies] = useState<Omit<CaseStudy, 'content'>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const res = await apiFetch<Omit<CaseStudy, 'content'>[]>('/api/v1/admin/case-studies');
    if (res.success) {
      setStudies(res.data);
    } else {
      setError(res.error?.message ?? 'Failed to load case studies.');
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function handleDelete(id: string, title: string) {
    if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
    setDeleting(id);
    const res = await apiFetch(`/api/v1/admin/case-studies/${id}`, { method: 'DELETE' });
    if (res.success) {
      setStudies((prev) => prev.filter((s) => s.id !== id));
    } else {
      alert(res.error?.message ?? 'Delete failed.');
    }
    setDeleting(null);
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Case Studies</h1>
          <p className="mt-0.5 text-sm text-zinc-400">{studies.length} total</p>
        </div>
        <Link
          to="/case-studies/new"
          className="flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
        >
          <Plus size={15} />
          New Case Study
        </Link>
      </div>

      {loading && <p className="text-sm text-zinc-500">Loading…</p>}
      {error   && <p className="text-sm text-red-400">{error}</p>}

      {!loading && !error && studies.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-700 py-16 text-center">
          <p className="text-sm text-zinc-500">No case studies yet.</p>
          <Link to="/case-studies/new" className="mt-2 inline-block text-sm text-indigo-400 hover:underline">
            Create your first one →
          </Link>
        </div>
      )}

      {!loading && studies.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-zinc-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 bg-zinc-900">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Tags</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Updated</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {studies.map((study) => (
                <tr key={study.id} className="border-b border-zinc-800/50 hover:bg-zinc-900/40">
                  <td className="px-4 py-3">
                    <p className="font-medium text-white">{study.title}</p>
                    <p className="font-mono text-xs text-zinc-500">{study.slug}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      study.status === 'published'
                        ? 'bg-green-500/10 text-green-400'
                        : 'bg-zinc-700/50 text-zinc-400'
                    }`}>
                      {study.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {study.tech_tags.slice(0, 3).map((t) => (
                        <span key={t} className="rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                    {new Date(study.updated_at).toLocaleDateString('en')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <Link
                        to={`/case-studies/${study.id}/edit`}
                        className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </Link>
                      <button
                        onClick={() => handleDelete(study.id, study.title)}
                        disabled={deleting === study.id}
                        className="rounded p-1.5 text-zinc-400 hover:bg-red-500/10 hover:text-red-400 disabled:opacity-40"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
